import { Table, Tag, Tooltip } from 'antd';
import { DatePickerFilter } from 'components/Filters/DatePickerFilter';
import { FilterIcon } from 'components/Filters/FilterIcon';
import { RangePickerFilter } from 'components/Filters/RangePickerFilter';
import { SelectTableFilter } from 'components/Filters/SelectTableFilter';
import { COLUMN_MEDIUM, PAGE_SIZE_OPTIONS, SCROLL_Y } from 'constants/table';
import { checkValue, formatDate, truncateString } from 'utils';
import {
  clientPaymentStatuses,
  clientPaymentStatusesSelect,
  paymentTypes,
  paymentTypesArrFilter,
} from 'utils/handbooks/paymentsHandbook';
import { filterColumns, priceRender, renderWithTooltip } from 'utils/table';

function ClientPaymentsTable({
  items,
  loading,
  current,
  pageSize,
  selectedRowIds,
  totalRows,
  onRowClick,
  onChange,
  onFilter,
  onRowSelect,
  filters = {},
  allowFilter = false,
  allowRowSelection = true,
}) {
  const { status, period, type, f_created, t_created } = filters;

  const columns = [
    {
      title: 'Клиент',
      key: 'company_name',
      dataIndex: 'company_name',
      width: 250,
      fixed: 'left',
      render: (value) => renderWithTooltip(value, 60),
    },
    {
      title: '№',
      key: 'id',
      dataIndex: 'id',
      width: 90,
    },
    {
      title: 'Тип',
      key: 'type',
      dataIndex: 'type',
      render: (type) => paymentTypes[type] || '---',
      filterDropdown: () => {
        return (
          <SelectTableFilter
            value={type}
            dataSourse={paymentTypesArrFilter}
            itemKey="value"
            itemValue="text"
            placeholder="Выберите тип"
            getSelectedItem={(type) => onFilter({ type })}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={type} />,
    },
    {
      title: 'Сумма',
      key: 'amount',
      dataIndex: 'amount',
      render: priceRender,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Статус',
      key: 'status',
      dataIndex: 'status',
      render: (status) => {
        if (!status || clientPaymentStatuses[status] === undefined) {
          return '--';
        }

        return (
          <Tooltip placement="topLeft" title={clientPaymentStatuses[status]}>
            <Tag>{truncateString(clientPaymentStatuses[status], 20)}</Tag>
          </Tooltip>
        );
      },
      filterDropdown: () => {
        return (
          <SelectTableFilter
            multiple
            value={status}
            dataSourse={clientPaymentStatusesSelect}
            itemKey="value"
            itemValue="text"
            placeholder="Выберите статус"
            getSelectedItem={(status) => onFilter({ status })}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={status.length} />,
    },
    {
      title: 'Комиссия',
      key: 'fee',
      dataIndex: 'fee',
      render: checkValue,
    },
    {
      title: 'Период',
      key: 'period',
      dataIndex: 'period',
      render: (value) => {
        const checkedValue = checkValue(value);

        if (value === checkedValue) {
          const month = checkedValue.slice(0, 2);
          const year = checkedValue.slice(2);
          return `${month}.${year}`;
        }

        return checkedValue;
      },
      filterDropdown: () => {
        return (
          <DatePickerFilter
            value={period}
            picker="month"
            format="MM.YYYY"
            returnFormat="YYYY-MM"
            getDate={(period) => onFilter({ period })}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={period} />,
    },
    {
      title: 'Создан',
      key: 'create_date',
      dataIndex: 'create_date',
      render: (date) => {
        if (date === checkValue(date)) {
          return formatDate(date, 'DD.MM.YYYY, HH.mm');
        }

        return checkValue(date);
      },
      filterDropdown: () => {
        return (
          <RangePickerFilter
            value={[f_created, t_created]}
            onFilterChange={([start, end]) => {
              onFilter({ f_created: start, t_created: end });
            }}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={f_created && t_created} />,
    },
    {
      title: 'Проведен',
      key: 'accept_date',
      dataIndex: 'accept_date',
      render: (date) => {
        if (date === checkValue(date)) {
          return formatDate(date);
        }

        return checkValue(date);
      },
    },
    {
      title: 'Выставлен',
      key: 'confirmation_date',
      dataIndex: 'confirmation_date',
      render: (date) => {
        if (date === checkValue(date)) {
          return formatDate(date);
        }

        return checkValue(date);
      },
    },
  ];

  return (
    <Table
      columns={filterColumns(columns, allowFilter)}
      dataSource={items}
      loading={loading}
      scroll={{ x: 1600, y: SCROLL_Y }}
      pagination={{
        current,
        total: totalRows,
        pageSize,
        showSizeChanger: true,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
      }}
      rowKey="id"
      onRow={(record) => {
        if (onRowClick) {
          return {
            onDoubleClick: () => onRowClick(record),
          };
        }

        return null;
      }}
      rowSelection={
        allowRowSelection
          ? {
              type: 'checkbox',
              selectedRowKeys: selectedRowIds,
              onChange: onRowSelect,
            }
          : false
      }
      onChange={onChange}
    />
  );
}

export { ClientPaymentsTable };
